import { useTranslation } from 'react-i18next'
import { motion, useReducedMotion } from 'framer-motion'
import { ArrowRight, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import Card from '../../../../components/Card'
import { usePageInfo } from '../../../../config/queries'
import {
  gameVersions,
  gameVersionHref,
  gameVersionLogo,
} from 'app/frontend/types/custom/GameVersion'
import Reveal from './Reveal'

const GameVersions = () => {
  const { t } = useTranslation()
  const reduceMotion = useReducedMotion()
  const {
    data: { gameVersion },
  } = usePageInfo()

  return (
    <section className='flex flex-col gap-8'>
      <Reveal className='flex flex-col items-center gap-2 text-center'>
        <h3 className='text-xl font-medium mb-0'>
          {t('homepage.versions-heading')}
        </h3>
      </Reveal>

      <div className='grid grid-cols-2 gap-4 sm:grid-cols-4'>
        {gameVersions.map((version, index) => {
          const isCurrent = version === gameVersion

          const card = (
            <Card
              className={cn(
                'flex h-full flex-col items-center gap-4 p-6 text-center transition-colors',
                isCurrent
                  ? 'border-primary ring-1 ring-primary'
                  : 'hover:border-primary/40',
              )}
            >
              <span className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                {t(`game-versions.${version}.caption`)}
              </span>
              <figure className='flex h-20 w-full items-center justify-center'>
                <img
                  src={gameVersionLogo(version)}
                  alt={t(`game-versions.${version}.name`)}
                  className='h-full max-w-full object-contain'
                />
              </figure>
              <span
                className={cn(
                  'mt-auto flex items-center gap-1.5 text-sm',
                  isCurrent ? 'text-primary' : 'text-muted-foreground',
                )}
              >
                {isCurrent ? (
                  <>
                    <Check className='h-4 w-4' />
                    {t('homepage.versions-current')}
                  </>
                ) : (
                  <>
                    {t('homepage.versions-switch')}
                    <ArrowRight className='h-4 w-4' />
                  </>
                )}
              </span>
            </Card>
          )

          return (
            <motion.div
              key={version}
              initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{
                duration: 0.4,
                delay: index * 0.06,
                ease: 'easeOut',
              }}
              whileHover={isCurrent || reduceMotion ? undefined : { y: -6 }}
            >
              {isCurrent ? (
                card
              ) : (
                <a href={gameVersionHref(version)} className='block h-full'>
                  {card}
                </a>
              )}
            </motion.div>
          )
        })}
      </div>
    </section>
  )
}

export default GameVersions
